import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import LandingPage from './pages/LandingPage';
import ServicesPage from './pages/ServicesPage';
import ApplicationPage from './pages/ApplicationPage';
import DashboardPage from './pages/DashboardPage';
import CalculatorsPage from './pages/CalculatorsPage';
import ComparePage from './pages/ComparePage';
import CreditScorePage from './pages/CreditScorePage';
import UtilitiesPage from './pages/UtilitiesPage';
import ReferralPage from './pages/ReferralPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import ContactUsPage from './pages/ContactUsPage';
import AboutPage from './pages/AboutPage';
import EnquiryPage from './pages/EnquiryPage';
import CreditCardsPage from './pages/CreditCardsPage';
import CreditCardDetailPage from './pages/CreditCardDetailPage';
import CreditCardComparePage from './pages/CreditCardComparePage';
import TaxServicesPage from './pages/TaxServicesPage';
import TaxServiceDetailPage from './pages/TaxServiceDetailPage';
import TaxAssistantPage from './pages/TaxAssistantPage';
import ConsultantPortalPage from './pages/ConsultantPortalPage';
import AdvertisePage from './pages/AdvertisePage';
import InvestorResearchPage from './pages/InvestorResearchPage';
import ConsultantSignupPage from './pages/ConsultantSignupPage';
import MobileBottomNav from './components/MobileBottomNav';
import ChatWidget from './components/ChatWidget';
import ShareFloatingButton from './components/ShareFloatingButton';
import GoogleAnalytics from './components/GoogleAnalytics';
import TaxAdBanner from './components/TaxAdBanner';
import MarketTickerBar from './components/MarketTickerBar';
import { Toaster } from './components/ui/sonner';
import { registerServiceWorker } from './utils/push';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('arthik_token');
    const storedUser = localStorage.getItem('arthik_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }
    setLoading(false);
    // Register service worker once for push notifications
    registerServiceWorker();
  }, []);

  const login = (token, user) => {
    setToken(token);
    setUser(user);
    localStorage.setItem('arthik_token', token);
    localStorage.setItem('arthik_user', JSON.stringify(user));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('arthik_token');
    localStorage.removeItem('arthik_user');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#FAFAF8' }}>
        <div className="text-lg" style={{ color: '#2D6A4F' }}>Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <BrowserRouter>
        <div className="App">
          <MarketTickerBar />
          <TaxAdBanner />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/apply/:serviceId" element={token ? <ApplicationPage /> : <Navigate to="/" />} />
            <Route path="/dashboard" element={token ? <DashboardPage /> : <Navigate to="/" />} />
            <Route path="/calculators" element={<CalculatorsPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/credit-score" element={<CreditScorePage />} />
            <Route path="/utilities" element={<UtilitiesPage />} />
            <Route path="/referrals" element={token ? <ReferralPage /> : <Navigate to="/" />} />
            <Route path="/admin" element={token && user?.role === 'admin' ? <AdminDashboardPage /> : <Navigate to="/" />} />
            <Route path="/contact" element={<ContactUsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/enquiry" element={<EnquiryPage />} />
            <Route path="/credit-cards" element={<CreditCardsPage />} />
            <Route path="/credit-cards/compare" element={<CreditCardComparePage />} />
            <Route path="/credit-cards/:id" element={<CreditCardDetailPage />} />
            <Route path="/tax-services" element={<TaxServicesPage />} />
            <Route path="/tax-services/assistant" element={<TaxAssistantPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            {/* PRIVATE / invite-only — NOT linked from public nav/footer. Share URL with investors directly. */}
            <Route path="/investor-research" element={<InvestorResearchPage />} />
            <Route path="/consultant/signup" element={<ConsultantSignupPage />} />
            <Route path="/tax-services/:slug" element={<TaxServiceDetailPage />} />
            <Route path="/client-portal" element={token && (user?.role === 'tax_consultant' || user?.role === 'admin') ? <ConsultantPortalPage /> : <Navigate to="/" />} />
            {/* SEO-friendly URLs — redirect to /tax-services/:slug */}
            <Route path="/itr-filing" element={<Navigate to="/tax-services/itr-filing" replace />} />
            <Route path="/gst-filing" element={<Navigate to="/tax-services/gst-filing" replace />} />
            <Route path="/tds-filing" element={<Navigate to="/tax-services/tds-filing" replace />} />
            <Route path="/tax-planning" element={<Navigate to="/tax-services/tax-planning" replace />} />
            <Route path="/business-registration" element={<Navigate to="/tax-services/business-registration" replace />} />
          </Routes>
          <MobileBottomNav />
          <ChatWidget />
          <ShareFloatingButton />
          <GoogleAnalytics />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
